import { useFetch } from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { FunctionComponent, HTMLAttributes, useState, useEffect, useCallback } from 'react';

import { useCustomRole } from '@api/v4/organizations/custom_permissions';
import { PlanGetResponse } from '@api/v4/resources/plan';

import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { DialogProvider } from '@components/common/providers/DialogContextProvider';
import { LimitsApiDataResponse, PlanLimitsApiDataResponse } from '@components/limits/customLimitsTypes';
import UsageInfoCard from '@components/limits/usage_info_card/UsageInfoCard';

import { AddCustomRoleCard } from './AddCustomRoleCard';
import { CreateCustomRolePage } from './CreateCustomRolePage';
import { CustomRoleManagerWrapper } from './CustomRoleManager';

interface CustomRoleUsageCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  organizationKey?: string;
  viewOnly?: boolean;
  hasSetLimits?: boolean;
  resourceType?: string;
  resourceKey?: string;
}

function getOpenValue(): boolean {
  const storageKey = `bf_new_custom_role_created_${BFG.organization_key}`;
  const value = window.localStorage?.getItem(storageKey);
  return value === '1';
}

export const CustomRoleUsageCard: FunctionComponent<CustomRoleUsageCardProps> = ({
  organizationKey = undefined,
  viewOnly = false,
  className = '',
  hasSetLimits = false,
  resourceType,
  resourceKey,
  ...props
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [customLimit, setCustomLimit] = useState<number>(0);
  const [customUsage, setCustomUsage] = useState<number>(0);
  const { customRole, loading, error, refreshCustomRole } = useCustomRole(organizationKey);

  const orgKey = organizationKey || BFG.organization_key;
  const customPermissionKey = customRole?.key;

  const [open, setOpen] = useState(getOpenValue());

  const limitsFeatureOn = BFG.hasFeature('resource_level_limit') && (BFG.organizationOwner !== false);
  const combinedRoleLimitsFeatureOn = BFG.hasFeature('combined_role_limits_rollout');

  const currentResourceType = resourceType || BFG.resource?.type || 'organization';
  const currentResourceKey = resourceKey || BFG.resource?.key;

  const customLimitsFetch = useFetch<LimitsApiDataResponse>({
    url: customPermissionKey ? `/api/v4/organizations/${orgKey}/limits/${currentResourceType}/collaborator/${customPermissionKey}?include=resource` : '',
    fetchOnMount: false,
  });

  const planLimitGetUrl = `/api/v4/organizations/${orgKey}/plan`;
  const planLimitFetch = useFetch<PlanLimitsApiDataResponse>({
    url: planLimitGetUrl,
    fetchOnMount: true,
  });

  // Fetch plan data for organization-level limits (when limits feature is off)
  const { response: planResponse } = useFetch<PlanGetResponse>({
    url: `/api/v4/${BFG.resource?.type || 'organizations'}s/${BFG.resource?.key || orgKey}/plan`,
    fetchOnMount: !limitsFeatureOn && !!customPermissionKey,
    fields: ['admins', 'collaborators', 'admin_invites', 'collaborator_invites', 'custom_permission_counts'],
  });

  const loadCustomLimits = useCallback(() => {
    if (customPermissionKey && orgKey) {
      customLimitsFetch.fetch();
    }
  }, [customPermissionKey, orgKey]);

  useEffect(() => {
    if (customPermissionKey && orgKey) {
      customLimitsFetch.fetch();
    }
  }, [customPermissionKey, orgKey, currentResourceType]);

  useEffect(() => {
    if (!customPermissionKey) {
      setCustomLimit(0);
      setCustomUsage(0);
      return;
    }

    if (limitsFeatureOn) {
      const resource = customLimitsFetch.response?.data?.find(
        (limit) => currentResourceKey === limit.relationships.resource.data.id
      )?.attributes;
      const resourceLimit = resource?.value || 0;
      setCustomLimit(resourceLimit);
      setCustomUsage(resource?.current_usage || 0);
    } else {
      const orgLimit = combinedRoleLimitsFeatureOn
        ? planResponse?.data?.attributes.limits.combined_roles
        : planResponse?.data?.attributes.limits.collaborators; // Fallback to collaborator limit
      setCustomLimit(orgLimit || 0);
      setCustomUsage(0);
    }
  }, [
    customPermissionKey,
    limitsFeatureOn,
    combinedRoleLimitsFeatureOn,
    customLimitsFetch.response,
    planResponse,
    currentResourceKey
  ]);

  const handleCreateRole = (): void => {
    setShowCreateForm(true);
  };

  const handleBackFromCreate = (): void => {
    setShowCreateForm(false);
    void refreshCustomRole();
    setOpen(getOpenValue());
  };

  const handleDelete = (): void => {
    void refreshCustomRole(); // Refresh after delete to update UI state
    document.getElementById('custom-role-usage-card')?.classList.remove('has-custom-role');
  };

  const handleSave = (): void => {
    document.getElementById('custom-role-usage-card')?.classList.add('has-custom-role');
  };

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  // Show create form if user clicked to create/edit
  if (showCreateForm) {
    return (
      <I18nProviderWrapper>
        <DialogProvider>
          <CustomRoleManagerWrapper>
            <CreateCustomRolePage
              existingRole={customRole}
              onBack={handleBackFromCreate}
              onDelete={handleDelete}
              onSave={handleSave}
              organizationKey={BFG.organization_key}
              userCount={customUsage}
            />
          </CustomRoleManagerWrapper>
        </DialogProvider>
      </I18nProviderWrapper>
    );
  }

  // Show "Add Custom Role" card if no role exists
  if (!customRole && !viewOnly) {
    return (
      <I18nProviderWrapper>
        <div className="alert well-sm level-limits-card j-custom-role-card" style={{ ...props.style, order: 4, margin: 0, flex: '0 0 205px' }}>
          <AddCustomRoleCard onNavigateToCreate={handleCreateRole} />
        </div>
      </I18nProviderWrapper>
    );
  }

  if (customRole) {
    let currentUsage = customUsage;
    let displayLimit: string | number = customLimit;

    if (limitsFeatureOn && currentResourceType !== 'collection') {
      displayLimit = customLimit || 0;
    } else {
      currentUsage = planResponse?.data?.attributes.custom_permission_counts[customRole.key] || 0;
      displayLimit = customLimit > 0 ? customLimit : '∞';
    }

    const usage = {
      current: currentUsage,
      limit: displayLimit,
    };

    // Compute amount unallocated across all resources for combined roles
    const maxTotal = planLimitFetch?.response?.data?.attributes.limits.combined_roles || 0;

    return (
      <I18nProviderWrapper>
        <div
          className={className ? className : 'alert well-sm level-limits-card j-user-limits'}
          {...props}
          style={{ ...props.style, order: 2 }}
        >
          <div id={`custom-role-card-${customRole.key}`}>
            <UsageInfoCard
              customClass="level-limits-card"
              editCapabilitiesTooltip={{
                open,
                onClose: () => {
                  try {
                    const storageKey = `bf_new_custom_role_created_${orgKey}`;
                    window.localStorage?.removeItem(storageKey);
                    setOpen(false);
                  } catch (_) { /* no-op */ }
                },
                message: t`Now you can start send out invitations and assign people with this new role.`
              }}
              editEnabled={true}
              hasSetLimits={hasSetLimits}
              icon="Person"
              isEnabled={true}
              newRoleTooltip={{
                open,
                onClose: () => {
                  try {
                    const storageKey = `bf_new_custom_role_created_${orgKey}`;
                    window.localStorage?.removeItem(storageKey);
                    setOpen(false);
                  } catch (_) { /* no-op */ }
                },
                maxTotal
              }}
              openEditDialog={handleCreateRole}
              orgKey={organizationKey}
              setLimitsAction={!viewOnly ? loadCustomLimits : undefined}
              subtitle={'0 pending invites'}
              title={customRole.label}
              usage={usage}
            />
          </div>
        </div>
      </I18nProviderWrapper>
    );
  }

  // If view_only and no role exists, show nothing
  return null;
};
